// lib/session-health.js - 세션 상태 모니터링 (Svelte 스토어 연결)
// 기존 session-health.js를 스토어와 연결하는 래퍼

import { get } from 'svelte/store';
import { session, updateConnectionStatus, updateSubscription, savePairingInfo, isRecovering } from '../stores/session.js';
import { WEB_VERSION } from '../main.js';
import { t } from '../i18n/index.js';
import { getDeviceName } from '../utils/device-info.js';
import { getPhoneToken } from './phone-token.js';
import { getPersistentPairings, checkDesktopStatus } from '../pairing.js';

const HEALTH_CHECK_INTERVAL = 60000; // 60초
let healthCheckTimer = null;
let consecutiveFailures = 0;
const MAX_CONSECUTIVE_FAILURES = 3;

// WebSocket
let ws = null;
let wsReconnectTimer = null;
let pingInterval = null;
const PING_INTERVAL = 30000; // 30 seconds

// Reconnection backoff
let reconnectAttempts = 0;
const MAX_RECONNECT_DELAY = 30000; // 30 seconds max
const BASE_RECONNECT_DELAY = 2000; // 2 seconds base

// 콜백 저장
let onDisconnectedCb = null;
let onPcStatusCb = null;

// PC 연결 상태
let pcConnectedViaWs = null;
let desktopChannel = null; // 'web' for Web Desktop, null for Flutter Desktop
let cachedDeviceName = null;
let versionLogged = false;

// WebSocket health tracking
let wsHealthy = false;
let lastWsMessage = 0;
const WS_HEALTH_TIMEOUT = 45000; // 45 seconds without message = unhealthy

// Browser lifecycle tracking
let lifecycleListenersActive = false;
let consecutiveWsFailures = 0;
const MAX_WS_FAILURES_BEFORE_HTTP = 5;

// Background time tracking
let backgroundStartTime = null;
const BACKGROUND_THRESHOLD = 10 * 60 * 1000; // 10 minutes

/**
 * Setup browser lifecycle listeners for recovery after backgrounding
 */
function setupLifecycleListeners() {
  if (lifecycleListenersActive) return;

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('online', handleOnline);
  window.addEventListener('pageshow', handlePageShow);
  lifecycleListenersActive = true;
  console.log('[Health] Lifecycle listeners active');
}

/**
 * Remove browser lifecycle listeners
 */
function teardownLifecycleListeners() {
  if (!lifecycleListenersActive) return;

  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('pageshow', handlePageShow);
  lifecycleListenersActive = false;

  if (recoverTimer) {
    clearTimeout(recoverTimer);
    recoverTimer = null;
  }
}

/**
 * Handle tab visibility changes (background/foreground)
 */
function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    backgroundStartTime = Date.now();
    return;
  }

  // visible
  const elapsed = backgroundStartTime ? Date.now() - backgroundStartTime : 0;
  backgroundStartTime = null;

  if (elapsed > BACKGROUND_THRESHOLD) {
    console.log(`[Health] Tab returned after ${Math.round(elapsed / 1000)}s (>${BACKGROUND_THRESHOLD / 1000}s), validating session`);
    validateAfterLongBackground();
  } else {
    console.log('[Health] Tab became visible, recovering connection');
    recoverConnection();
  }
}

/**
 * Handle network coming back online
 */
function handleOnline() {
  console.log('[Health] Network online, recovering connection');
  recoverConnection();
}

/**
 * Handle page restore from bfcache
 */
function handlePageShow(event) {
  if (event.persisted) {
    console.log('[Health] Page restored from bfcache, recovering connection');
    recoverConnection();
  }
}

/**
 * Common recovery logic: reset backoff, reconnect WS, fire immediate health check.
 * Debounced to avoid multiple rapid lifecycle events (visibility + online + pageshow)
 * firing simultaneously when phone returns from background.
 */
let recoverTimer = null;
function recoverConnection() {
  if (recoverTimer) return; // Already scheduled

  isRecovering.set(true);

  recoverTimer = setTimeout(async () => {
    recoverTimer = null;
    reconnectAttempts = 0;

    // Reconnect WebSocket if not open
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      connectWebSocket();
    }

    // Fire immediate HTTP health check to catch expired sessions
    try {
      await performHealthCheck(true);
    } finally {
      isRecovering.set(false);
    }
  }, 500);
}

/**
 * Recovery after long background (>10 min): validate session via HTTP before
 * allowing any uploads. Skips the debounce delay used by recoverConnection()
 * since we need an immediate answer.
 */
async function validateAfterLongBackground() {
  isRecovering.set(true);

  // Reconnect WebSocket (it's almost certainly dead after 10+ min)
  reconnectAttempts = 0;
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    connectWebSocket();
  }

  // Immediate HTTP health check — the result determines whether to show
  // DisconnectedDialog (session gone) or clear the overlay (session alive).
  try {
    await performHealthCheck(true);
  } finally {
    isRecovering.set(false);
  }
}

/**
 * Calculate reconnection delay with exponential backoff
 */
function getReconnectDelay() {
  const delay = Math.min(
    BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts),
    MAX_RECONNECT_DELAY
  );
  return delay;
}

/**
 * 세션 상태 모니터링 시작
 * @param {Function} onDisconnected - 연결 끊김 시 콜백 (reason: string)
 * @param {Function} onPcStatus - PC 온/오프라인 콜백 (boolean, deviceName)
 */
export function startSessionHealthCheck(onDisconnected, onPcStatus = null) {
  // If already running with active WebSocket, just update callbacks
  if (ws && ws.readyState === WebSocket.OPEN) {
    onDisconnectedCb = onDisconnected;
    onPcStatusCb = onPcStatus;
    console.log('Session health check already running, updated callbacks');
    return;
  }

  stopSessionHealthCheck();

  consecutiveFailures = 0;
  pcConnectedViaWs = null;
  cachedDeviceName = null;
  versionLogged = false;
  onDisconnectedCb = onDisconnected;
  onPcStatusCb = onPcStatus;

  // WebSocket 연결 (주요 채널)
  connectWebSocket();

  // Browser lifecycle listeners (visibility, online, pageshow)
  setupLifecycleListeners();

  // HTTP fallback: 첫 체크는 5초 후 (항상 실행 - 구독 정보 등 가져오기)
  healthCheckTimer = setTimeout(() => {
    performHealthCheck(true); // Initial check - always run
    // Periodic checks - only when WebSocket is not healthy
    healthCheckTimer = setInterval(() => {
      if (!isWsHealthy()) {
        performHealthCheck(false);
      } else {
        console.log('[Health] Skipping periodic poll - WebSocket healthy');
      }
    }, HEALTH_CHECK_INTERVAL);
  }, 5000);

  console.log('Session health check started');
}

/**
 * 세션 상태 모니터링 중지
 */
export function stopSessionHealthCheck() {
  if (healthCheckTimer) {
    clearInterval(healthCheckTimer);
    clearTimeout(healthCheckTimer);
    healthCheckTimer = null;
  }
  // Clear ping interval
  if (pingInterval) {
    clearInterval(pingInterval);
    pingInterval = null;
  }
  teardownLifecycleListeners();
  disconnectWebSocket();
  consecutiveFailures = 0;
  consecutiveWsFailures = 0;
  reconnectAttempts = 0; // Reset backoff for clean state
  backgroundStartTime = null;
  pcConnectedViaWs = null;
  desktopChannel = null;
  cachedDeviceName = null;
  wsHealthy = false;
  lastWsMessage = 0;
  isRecovering.set(false);
  onDisconnectedCb = null;
  onPcStatusCb = null;
}

/**
 * WebSocket 연결
 */
function connectWebSocket() {
  disconnectWebSocket();

  const pairingInfo = get(session);
  if (!pairingInfo) return;

  try {
    const wsBase = pairingInfo.endpoint.replace(/^http/, 'ws');
    const deviceName = getDeviceName();
    const wsUrl = `${wsBase}/sessions/${pairingInfo.device_id}/ws?client=web&deviceName=${encodeURIComponent(deviceName)}&version=${encodeURIComponent(WEB_VERSION)}`;

    ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      reconnectAttempts = 0; // Reset backoff on successful connection
      consecutiveWsFailures = 0;
      wsHealthy = true;
      console.log('[ScanToDesk] WebSocket connected');
      console.log(`  Web: v${WEB_VERSION}`);
      consecutiveFailures = 0;

      // Start ping interval
      if (pingInterval) clearInterval(pingInterval);
      pingInterval = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, PING_INTERVAL);

      // Register phone token for trial anchoring
      registerPhoneToken();
    };

    ws.onmessage = (event) => {
      lastWsMessage = Date.now();
      wsHealthy = true;
      try {
        const msg = JSON.parse(event.data);
        handleWsMessage(msg);
      } catch (e) {
        // ignore parse errors
      }
    };

    ws.onclose = () => {
      wsHealthy = false;
      ws = null;
      consecutiveWsFailures++;

      // Clear ping interval
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }

      const delay = getReconnectDelay();
      reconnectAttempts++;

      console.log(`[Health] WebSocket closed, reconnecting in ${delay}ms (attempt ${reconnectAttempts}, wsFailures ${consecutiveWsFailures})`);

      // Force HTTP health check after repeated WS failures (catches expired sessions while backgrounded)
      if (consecutiveWsFailures >= MAX_WS_FAILURES_BEFORE_HTTP) {
        console.log('[Health] Too many WS failures, forcing HTTP health check');
        performHealthCheck(true);
      }

      if (onDisconnectedCb) {
        wsReconnectTimer = setTimeout(connectWebSocket, delay);
      }
    };

    ws.onerror = (err) => {
      console.error('Web WebSocket error:', err);
      wsHealthy = false;
    };
  } catch (e) {
    console.error('Failed to connect WebSocket:', e);
  }
}

/**
 * WebSocket 메시지 처리
 */
function handleWsMessage(msg) {
  switch (msg.type) {
    case 'connected':
      // Message format: { type: 'connected', data: { clientType, channel, disconnected, ... } }
      if (msg.data?.clientType === 'desktop') {
        if (msg.data.disconnected) {
          pcConnectedViaWs = false;
          desktopChannel = null;
          updateConnectionStatus('disconnected');
          if (onPcStatusCb) onPcStatusCb(false, cachedDeviceName);
        } else {
          pcConnectedViaWs = true;
          desktopChannel = msg.data.channel || null; // 'web' for Web Desktop
          updateConnectionStatus('connected');
          if (onPcStatusCb) onPcStatusCb(true, cachedDeviceName);
        }
      }
      break;

    case 'session_expired':
      console.log('Session expired via WebSocket');
      const expiredCb = onDisconnectedCb; // Save ref before stop nulls it
      stopSessionHealthCheck();
      if (expiredCb) expiredCb('session_expired');
      break;

    case 'trial_status':
      // Trial status is phone-based; subscription is set authoritatively
      // by the HTTP health check (which uses the desktop fingerprint).
      // Only upgrade to pro here — never downgrade to free.
      if (msg.data?.plan === 'pro') {
        updateSubscription({
          plan: 'pro',
          dailyUploadLimit: -1,
        });
      }
      break;

    case 'pong':
      break;
  }
}

/**
 * WebSocket 연결 해제
 */
function disconnectWebSocket() {
  if (wsReconnectTimer) {
    clearTimeout(wsReconnectTimer);
    wsReconnectTimer = null;
  }
  if (ws) {
    ws.onclose = null;
    ws.close();
    ws = null;
  }
}

/**
 * Register phone token with the session for trial anchoring
 */
async function registerPhoneToken() {
  try {
    const pairingInfo = get(session);
    if (!pairingInfo) return;

    const phoneToken = await getPhoneToken();

    const response = await fetch(
      `${pairingInfo.endpoint}/sessions/${pairingInfo.device_id}/phone-token`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${pairingInfo.credentials.apiKey}`,
        },
        body: JSON.stringify({ phoneToken }),
      }
    );

    if (response.ok) {
      console.log('[ScanToDesk] Phone token registered');
    }
  } catch (e) {
    console.warn('[ScanToDesk] Phone token registration failed:', e);
  }
}

/**
 * Get the current WebSocket instance for direct binary relay
 */
export function getWebSocket() {
  return ws;
}

/**
 * Get the connected desktop's channel ('web' for Web Desktop, null for Flutter)
 */
export function getDesktopChannel() {
  return desktopChannel;
}

/**
 * Check if WebSocket is healthy (connected and recently received messages)
 */
export function isWsHealthy() {
  if (!wsHealthy) return false;
  if (Date.now() - lastWsMessage > WS_HEALTH_TIMEOUT) {
    wsHealthy = false;
    return false;
  }
  return true;
}

/**
 * HTTP 헬스 체크 (WebSocket fallback)
 * @param {boolean} isInitial - 초기 체크 여부 (true면 항상 실행)
 */
async function performHealthCheck(isInitial = false) {
  // Skip polling if WebSocket is healthy and we have PC status (unless initial check)
  if (!isInitial && isWsHealthy() && pcConnectedViaWs !== null) {
    console.log('[Health] Skipping poll - WebSocket healthy');
    return;
  }

  const result = await checkSessionHealth();

  if (!result.valid) {
    const cb = onDisconnectedCb;
    stopSessionHealthCheck();
    if (cb) cb(result.reason);
    return;
  }

  if (result.deviceName) {
    cachedDeviceName = result.deviceName;
  }

  if (onPcStatusCb) {
    onPcStatusCb(result.pcOnline, cachedDeviceName);
  }
}

/**
 * 세션 상태 HTTP 체크
 */
async function checkSessionHealth() {
  const pairingInfo = get(session);

  if (!pairingInfo) {
    return { valid: false, pcOnline: false, reason: 'no_pairing' };
  }

  try {
    const deviceName = getDeviceName();
    const response = await fetch(
      `${pairingInfo.endpoint}/sessions/${pairingInfo.device_id}/status`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${pairingInfo.credentials.apiKey}`,
          'X-Device-Name': deviceName,
          'X-Web-Version': WEB_VERSION
        }
      }
    );

    if (response.ok) {
      const data = await response.json();
      consecutiveFailures = 0;

      if (data.versions && !versionLogged) {
        versionLogged = true;
        console.log('[ScanToDesk] Connected');
        console.log(`  Worker: v${data.versions.worker || 'unknown'}`);
        console.log(`  Desktop: v${data.versions.desktop || 'unknown'}`);
        console.log(`  Web: v${WEB_VERSION}`);
      }

      if (data.subscription) {
        updateSubscription(data.subscription);
      }

      return {
        valid: true,
        pcOnline: data.pcOnline || data.desktopConnected || false,
        expiresAt: data.expiresAt,
        versions: data.versions,
        subscription: data.subscription,
        deviceName: data.deviceName || null
      };
    }

    if (response.status === 401) {
      return { valid: false, pcOnline: false, reason: 'unauthorized' };
    }
    if (response.status === 404) {
      return { valid: false, pcOnline: false, reason: 'session_not_found' };
    }
    if (response.status === 410) {
      return { valid: false, pcOnline: false, reason: 'session_expired' };
    }

    return { valid: false, pcOnline: false, reason: 'unknown_error' };
  } catch (e) {
    consecutiveFailures++;
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      return { valid: false, pcOnline: false, reason: 'network_error' };
    }
    return { valid: true, pcOnline: false, reason: 'temporary_network_issue' };
  }
}

/**
 * Attempt to reconnect to desktop via persistent pairing (quick version).
 * Tries immediately + one retry after 5s. Used for lifecycle recovery.
 * @returns {Promise<boolean>} true if reconnect succeeded
 */
export async function attemptReconnectViaPairing() {
  // Try immediately, then retry once after delay for heartbeat propagation
  const result = await _tryPairingReconnect();
  if (result) return true;

  console.log('[Health] First pairing attempt failed, retrying in 5s (waiting for heartbeat)');
  await new Promise(r => setTimeout(r, 5000));
  return await _tryPairingReconnect();
}

/**
 * Poll for desktop reconnect via persistent pairing over ~30 seconds.
 * Retries every 5 seconds, giving the desktop time to restart and heartbeat.
 * Used when session is lost — shows "reconnecting" status instead of immediate dialog.
 * @returns {Promise<boolean>} true if reconnect succeeded
 */
export async function attemptReconnectPolling() {
  const MAX_DURATION = 30000; // 30 seconds total
  const RETRY_INTERVAL = 5000; // 5 seconds between retries
  const startTime = Date.now();
  let attempt = 0;

  while (Date.now() - startTime < MAX_DURATION) {
    attempt++;
    const elapsed = Math.round((Date.now() - startTime) / 1000);
    console.log(`[Health] Pairing reconnect poll #${attempt} (${elapsed}s elapsed)`);

    const result = await _tryPairingReconnect();
    if (result) {
      console.log(`[Health] Pairing reconnect succeeded on attempt #${attempt}`);
      return true;
    }

    const remaining = MAX_DURATION - (Date.now() - startTime);
    if (remaining <= RETRY_INTERVAL) break;

    await new Promise(r => setTimeout(r, RETRY_INTERVAL));
  }

  console.log('[Health] Pairing reconnect polling exhausted after 30s');
  return false;
}

async function _tryPairingReconnect() {
  const pairings = getPersistentPairings();
  if (pairings.length === 0) {
    console.log('[Health] No persistent pairings for reconnect');
    return false;
  }

  for (const pairing of pairings) {
    try {
      console.log(`[Health] Checking pairing ${pairing.pairingId} for reconnect`);
      const status = await checkDesktopStatus(pairing.pairingId);

      if (status.online && status.session) {
        console.log(`[Health] Reconnecting via pairing: ${pairing.pairingId}`);
        const pairingInfo = {
          device_id: status.session.sessionId,
          endpoint: status.session.endpoint,
          credentials: { apiKey: status.session.apiKey },
          desktop_name: status.desktopName || pairing.desktopName || null,
          desktop_fingerprint: pairing.desktopFingerprint || null,
          paired_at: new Date().toISOString(),
        };

        if (savePairingInfo(pairingInfo)) {
          return true;
        }
      }
    } catch (e) {
      console.warn(`[Health] Pairing ${pairing.pairingId} check failed:`, e);
    }
  }

  console.log('[Health] No online desktop found via pairings');
  return false;
}

/**
 * 연결 끊김 사유를 사용자 친화적 메시지로 변환
 */
export function getDisconnectMessage(reason) {
  const reasonKeyMap = {
    no_pairing: 'noPairing',
    unauthorized: 'unauthorized',
    session_not_found: 'sessionNotFound',
    session_expired: 'sessionExpired',
    network_error: 'networkError',
    unknown_error: 'unknownError'
  };

  const key = reasonKeyMap[reason] || 'unknownError';

  return {
    title: t(`disconnect.${key}.title`),
    message: t(`disconnect.${key}.message`)
  };
}
